import { Dispatch, KeyboardEvent, RefObject, SetStateAction } from 'react';
import { FloatStepperHandle } from '../components/FloatStepper/FloatStepper';
import { useTranslate } from '../../../../hooks/context/useTranslate';
import { coerceLayoutFieldValue } from '../../../../utils/layouts/coerceLayoutFieldValue';
import { validateLayoutFieldValue } from '../../../../utils/layouts/validateLayoutFieldValue';
import {
  FieldHandlerCallbacks,
  FilterError,
  FormItem,
  FullscreenReferenceType,
  GridInputValue,
  ReferenceControl,
} from '../../../../types/types';

type UseSettingsPanelItemHandlersOptions = {
  item: FormItem;
  value: GridInputValue;
  showStepper: boolean;
  stepperRef: RefObject<FloatStepperHandle | null>;
  handlerCallbacks: FieldHandlerCallbacks;
  setError: Dispatch<SetStateAction<FilterError>>;
  onChange: (name: string, value: GridInputValue) => void;
  onApplyDefault?: (name: string, value: GridInputValue) => void;
  onOpenFullscreenReference?: (reference: FullscreenReferenceType) => void;
};

// Glue handlers of SettingsPanelItem: value changes (coerce + validate +
// handler triggers), the numeric-input arrow-key stepping, and opening the
// fullscreen reference picker.
export const useSettingsPanelItemHandlers = ({
  item,
  value,
  showStepper,
  stepperRef,
  handlerCallbacks,
  setError,
  onChange,
  onApplyDefault,
  onOpenFullscreenReference,
}: UseSettingsPanelItemHandlersOptions) => {
  const { t } = useTranslate();
  const name = item.name ?? '';

  const handleChange = (
    nextValue: GridInputValue,
    propagateHandler: boolean = true
  ): void => {
    const coercedValue = coerceLayoutFieldValue(item.type, nextValue);

    // Programmatic writes (mount-time defaults / value normalization inside
    // GridInput) go through onApplyDefault — they update the state without
    // counting as a user change (baseline is updated along).
    if (!propagateHandler) {
      onApplyDefault?.(name, coercedValue);
      return;
    }

    // Invalid values are written too — blocking them would break typing in the
    // controlled input. The error only marks the field; the save validates the
    // whole layout before the PUT (validateLayoutContent).
    const errorKey = validateLayoutFieldValue(name, coercedValue);
    setError(errorKey ? [t(errorKey)] : null);

    onChange(name, coercedValue);
    // User change fires the field's handlerTriggers (programmatic defaults
    // returned above on purpose — parity with dataset forms).
    handlerCallbacks.onChange?.(coercedValue);
  };

  // ArrowUp/ArrowDown inside the numeric input step the value; a held key
  // drives the same hold cadence as a held stepper button. Swallowed locally
  // so the caret doesn't jump and the canvas move-object shortcut can't fire.
  const handleInputRowKeyDown = (event: KeyboardEvent): void => {
    if (
      !showStepper ||
      (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // The OS key auto-repeat re-fires keydown — the stepper runs its own
    // repeat cadence from the first press.
    if (event.repeat) {
      return;
    }

    stepperRef.current?.startHold(event.key === 'ArrowUp' ? 1 : -1);
  };

  const handleInputRowKeyUp = (event: KeyboardEvent): void => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      stepperRef.current?.stopHold();
    }
  };

  // ReferenceInput pre-resolves the JSONata filter at click time: null = strip
  // the raw filter, undefined = keep the original (same contract as dataset forms).
  const handleOpenFullscreenReference = (
    resolvedFilter?: Record<string, unknown> | null
  ): void => {
    if (item.control?.type !== 'reference' || !onOpenFullscreenReference) {
      return;
    }

    let effectiveControl = item.control as ReferenceControl;
    if (resolvedFilter === null) {
      effectiveControl = { ...effectiveControl, filter: undefined };
    } else if (resolvedFilter !== undefined) {
      effectiveControl = { ...effectiveControl, filter: resolvedFilter };
    }

    onOpenFullscreenReference({
      name,
      datasetId: effectiveControl.datasetId,
      control: effectiveControl,
      value: value === null || value === undefined ? '' : String(value),
      label: item.label || name,
      isFormInput: true,
    });
  };

  return {
    handleChange,
    handleInputRowKeyDown,
    handleInputRowKeyUp,
    handleOpenFullscreenReference,
  };
};
